import { Edit } from '@carbon/react/icons';
import { ColumnAction } from './types';
import styles from './cell-action-button.module.scss';

// ─────────────────────────────────────
// Props
// ─────────────────────────────────────

interface CellActionButtonProps<TData> {
  action: ColumnAction<TData>;
  row: TData;
}

// ─────────────────────────────────────
// Component
// ─────────────────────────────────────

/**
 * Inline action button rendered within table cells.
 * Prevents click event bubbling to avoid triggering row selection.
 * @param {CellActionButtonProps} props - Component props
 * @param {ColumnAction} props.action - Action configuration with icon, label, and onClick
 * @param {TData} props.row - Row data passed to onClick handler
 * @return {JSX.Element} The action button element
 */
export function CellActionButton<TData>({
  action,
  row,
}: CellActionButtonProps<TData>) {
  const { icon, label, onClick } = action;

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Don't trigger row selection
    onClick(row);
  };

  return (
    <button
      type="button"
      className={styles.cellActionButton}
      onClick={handleClick}
      aria-label={label}
      title={label}
    >
      {icon ?? <Edit />}
    </button>
  );
}
